import * as notificationsApi from '@/api/endpoints/notifications';
import { useFriendActions } from '@/features/friends/hooks/use-friend-actions';
import { useFriendRequests } from '@/features/friends/hooks/use-friend-requests';
import { NOTIFICATIONS_QUERY_KEYS, useNotifications } from '@/features/notifications/hooks/use-notifications';
import { Notification } from '@/types';
import { NotificationsData } from '@/types/display';
import { createLogger } from '@/utils/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { router, useFocusEffect } from 'expo-router';
import { useCallback, useMemo, useRef, useState } from 'react';

const logger = createLogger('NOTIFICATIONS');

function useOptimisticMarkAsRead(queryClient: ReturnType<typeof useQueryClient>) {
  const onError = useCallback(
    (_err: unknown, _id: string | undefined, context: { previousData?: NotificationsData } | undefined) => {
      if (context?.previousData) {
        queryClient.setQueryData(NOTIFICATIONS_QUERY_KEYS.list(), context.previousData);
      }
      logger.error('Failed to mark notification(s) as read');
    },
    [queryClient],
  );

  const onSettled = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.list() });
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.unreadCount() });
  }, [queryClient]);

  const markAllAsRead = useMutation({
    mutationFn: async () => { await notificationsApi.markAllAsRead(); },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.list() });
      const previousData = queryClient.getQueryData<NotificationsData>(NOTIFICATIONS_QUERY_KEYS.list());
      queryClient.setQueryData<NotificationsData>(
        NOTIFICATIONS_QUERY_KEYS.list(),
        (oldData) => oldData ? { ...oldData, notifications: oldData.notifications.map((n) => ({ ...n, read: true })) } : oldData,
      );
      return { previousData };
    },
    onError,
    onSettled,
  });

  const markAsRead = useMutation({
    mutationFn: async (id: string) => { await notificationsApi.markAsRead(id); },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.list() });
      const previousData = queryClient.getQueryData<NotificationsData>(NOTIFICATIONS_QUERY_KEYS.list());
      queryClient.setQueryData<NotificationsData>(
        NOTIFICATIONS_QUERY_KEYS.list(),
        (oldData) => oldData ? { ...oldData, notifications: oldData.notifications.map((n) => n.id === id ? { ...n, read: true } : n) } : oldData,
      );
      return { previousData };
    },
    onError,
    onSettled,
  });

  return { markAllAsRead, markAsRead };
}

export function useNotificationActions() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useNotifications();
  const { data: friendRequestsData } = useFriendRequests();
  const { acceptRequest, rejectRequest } = useFriendActions();
  const { markAllAsRead, markAsRead } = useOptimisticMarkAsRead(queryClient);

  const [processingFriendRequestIds, setProcessingFriendRequestIds] = useState<Set<string>>(new Set());
  const [friendRequestActionOverrides, setFriendRequestActionOverrides] = useState<
    Record<string, 'accepted' | 'rejected'>
  >({});

  const requesterToRequestIdMap = useMemo(() => {
    const map = new Map<string, string>();
    friendRequestsData?.forEach((request) => map.set(request.requesterId, request.id));
    return map;
  }, [friendRequestsData]);

  const actionableFriendRequestNotificationIds = useMemo(() => {
    const ids = new Set<string>();
    data?.notifications?.forEach((notif) => {
      if (notif.type === 'friend_request' && notif.user?.id && requesterToRequestIdMap.has(notif.user.id)) {
        ids.add(notif.id);
      }
    });
    return ids;
  }, [data?.notifications, requesterToRequestIdMap]);

  const hasAutoMarkedRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      const currentData = queryClient.getQueryData<NotificationsData>(NOTIFICATIONS_QUERY_KEYS.list());
      const hasUnread = currentData?.notifications?.some((n) => !n.read) ?? false;

      if (!hasUnread || hasAutoMarkedRef.current) return;

      const timerId = setTimeout(() => {
        const latestData = queryClient.getQueryData<NotificationsData>(NOTIFICATIONS_QUERY_KEYS.list());
        if (latestData?.notifications?.some((n) => !n.read)) {
          markAllAsRead.mutate(undefined);
          hasAutoMarkedRef.current = true;
        }
      }, 2000);

      return () => {
        clearTimeout(timerId);
        hasAutoMarkedRef.current = false;
      };
    }, [queryClient, markAllAsRead]),
  );

  const handleNotificationPress = useCallback((notification: Notification) => {
    if (notification.referenceType === 'check_in' && notification.referenceId) {
      router.push({ pathname: '/(root)/check-in/[id]', params: { id: notification.referenceId } });
    } else if (
      (notification.type === 'list_invitation' ||
        notification.type === 'list_invitation_accepted' ||
        notification.type === 'list_item_added') &&
      notification.listId
    ) {
      router.push({ pathname: '/(root)/lists/[id]', params: { id: notification.listId } });
    }
  }, []);

  const handleFriendRequestAction = useCallback(
    async (notificationId: string, requesterId: string, action: 'accepted' | 'rejected') => {
      const requestId = requesterToRequestIdMap.get(requesterId);
      if (!requestId) {
        logger.error('Friend request ID not found for requester:', requesterId);
        return;
      }

      setProcessingFriendRequestIds((prev) => new Set(prev).add(notificationId));
      setFriendRequestActionOverrides((prev) => ({ ...prev, [notificationId]: action }));

      const friendAction = action === 'accepted' ? acceptRequest : rejectRequest;

      try {
        queryClient.setQueryData<NotificationsData>(
          NOTIFICATIONS_QUERY_KEYS.list(),
          (oldData) =>
            oldData
              ? {
                  ...oldData,
                  notifications: oldData.notifications.map((n) =>
                    n.id === notificationId ? { ...n, read: true, friendRequestAction: action } : n,
                  ),
                }
              : oldData,
        );

        await Promise.all([
          new Promise<void>((resolve, reject) => {
            friendAction.mutate(requestId, {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['friends', 'requests'] as const });
                resolve();
              },
              onError: (err) => reject(err),
            });
          }),
          markAsRead.mutateAsync(notificationId),
        ]);
      } catch {
        logger.error(`Failed to ${action === 'accepted' ? 'accept' : 'reject'} friend request`);
        setFriendRequestActionOverrides((prev) => {
          const next = { ...prev };
          delete next[notificationId];
          return next;
        });
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEYS.list() });
      } finally {
        setProcessingFriendRequestIds((prev) => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
      }
    },
    [requesterToRequestIdMap, acceptRequest, rejectRequest, queryClient, markAsRead],
  );

  const notifications = useMemo(() => {
    const base = data?.notifications || [];
    return base.map((notif) => {
      const override = friendRequestActionOverrides[notif.id];
      return override ? { ...notif, friendRequestAction: override } : notif;
    });
  }, [data?.notifications, friendRequestActionOverrides]);

  return {
    notifications,
    isLoading,
    error,
    refetch,
    actionableFriendRequestNotificationIds,
    processingFriendRequestIds,
    handleMarkAsRead: (id: string) => markAsRead.mutate(id),
    handleMarkAllAsRead: () => markAllAsRead.mutate(undefined),
    handleNotificationPress,
    handleAcceptFriendRequest: (notificationId: string, requesterId: string) =>
      handleFriendRequestAction(notificationId, requesterId, 'accepted'),
    handleRejectFriendRequest: (notificationId: string, requesterId: string) =>
      handleFriendRequestAction(notificationId, requesterId, 'rejected'),
  };
}
